/**
 * DepartureFormDocument — on-screen preview of the departure form.
 * Supports local row deletion (doesn't alter underlying data) and editable departure notes.
 */
import React, { useState, useEffect } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const GREEN_DOOR_LOGO = 'https://customer-assets.emergentagent.com/job_thoroughbred-deals/artifacts/xmrjz10b_Powered%20By%20Green%20Door.png';

function fmtDate(s) {
  if (!s) return '—';
  try {
    const dt = new Date(s.includes('T') ? s : s + 'T12:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return s; }
}

function fmtDateTime(dateStr, timeStr) {
  if (!dateStr) return '—';
  let label = fmtDate(dateStr);
  if (timeStr) {
    try {
      const [h, m] = timeStr.split(':').map(Number);
      label += ` @ ${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
    } catch { /* ignore */ }
  }
  return label;
}

function buildVices(horse) {
  const v = [];
  if (horse.vices_cribber)             v.push('Cribber');
  if (horse.vices_weaving)             v.push('Weaving');
  if (horse.vices_stall_walking)       v.push('Stall Walker');
  if (horse.vices_pawing)              v.push('Pawing');
  if (horse.vices_wood_chewing)        v.push('Wood Chewing');
  if (horse.vices_wall_kicking)        v.push('Wall Kicking');
  if (horse.other_issues_eye_certification) v.push('Eye Cert Issue');
  if (horse.other_issues_surgery)      v.push('Surgery');
  if (horse.other_issues_regumate)     v.push('Regumate');
  return v.length ? v.join(' · ') : 'None';
}

// Capitalise horse type: all-caps acronyms, else sentence case
function fmtHorseType(raw) {
  if (!raw) return '';
  const s = raw.replace(/_/g, ' ');
  // Known all-caps codes
  const acronyms = ['hora', 'ntra', 'ebf', 'ktdf'];
  const words = s.split(' ');
  return words.map(w => acronyms.includes(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

export default function DepartureFormDocument({ data, isApproved = false }) {
  const navigate = useNavigate();
  const {
    horse = {}, travel = {}, dest_entity: destEnt, tenant = {},
    owners = [], cover_records: covers = [], health_records: health = [],
    health_date_from: hFrom, health_date_to: hTo,
  } = data;

  const [deletedBreeding, setDeletedBreeding] = useState(new Set());
  const [deletedHealth, setDeletedHealth]     = useState(new Set());
  const [depNotes, setDepNotes]               = useState('');
  const [editingNotes, setEditingNotes]       = useState(false);

  // Reset local state when data changes
  useEffect(() => {
    setDeletedBreeding(new Set());
    setDeletedHealth(new Set());
    setDepNotes('');
    setEditingNotes(false);
  }, [data]);

  const destAddr = destEnt?.primary_address || {};
  const destName = destEnt?.system_name || travel.travel_destination_name || travel.travel_dest_other || '—';
  const destPhone = destEnt?.primary_phone || '';

  const tAddr = {
    name:   tenant.name || '',
    street: tenant.address_street || '',
    city:   tenant.address_city || '',
    state:  tenant.address_state || '',
    zip:    tenant.address_zip || '',
    phone:  tenant.phone || '',
  };

  const dateRange = (hFrom || hTo) ? `${hFrom ? fmtDate(hFrom) : ''} – ${hTo ? fmtDate(hTo) : ''}` : '';

  const th = 'border border-gray-400 px-2 py-1 text-left text-xs font-bold bg-gray-100';
  const td = 'border border-gray-300 px-2 py-1 text-xs align-top';

  const visibleCovers = covers.filter((_, i) => !deletedBreeding.has(i));
  const visibleHealth = health.filter((_, i) => !deletedHealth.has(i));

  return (
    <div className="border border-gray-200 rounded bg-white shadow-sm max-w-[820px] mx-auto">
    <div className="bg-white p-6 max-w-[800px] mx-auto font-sans text-sm" data-testid="departure-form-document">

      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="w-20 h-16 flex items-center justify-center text-[10px] text-gray-400">
          {tenant.logo_url
            ? <img src={tenant.logo_url} alt="logo" className="max-h-14 max-w-full object-contain" />
            : <span className="text-gray-300 text-[9px]">[Logo]</span>}
        </div>
        <div className="text-right">
          <h1 className="text-2xl font-bold text-primary">Departure Form</h1>
          <p className="font-semibold text-sm mt-0.5">{tenant.name || ''}</p>
          <p className="text-xs text-gray-500">{tenant.reply_to_email || tenant.email || ''}</p>
          <p className="text-xs text-gray-500">{tenant.phone || ''}</p>
        </div>
      </div>

      {/* Gray rule above horse box */}
      <hr className="border-gray-400 mb-0" />

      {/* Horse section */}
      <div className="bg-[#d4e4d4] p-3 mb-3">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-bold text-base">Horse: {horse.system_name || horse.name || '—'}</p>
              {!isApproved && (
              <button
                onClick={() => navigate(`/horses/${horse.id}`)}
                title="Edit horse profile"
                className="text-gray-500 hover:text-primary">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              )}
            </div>
            <p className="text-xs text-gray-600 capitalize">
              {(horse.color || '').replace(/_/g, '/')} {horse.gender}
            </p>
            <p className="text-xs text-gray-600">
              <span className="font-bold">DOB:</span> {fmtDate(horse.birthdate)}
              {' · '}
              <span className="font-bold">Horse Type:</span>{' '}
              {fmtHorseType(horse.horse_type_female || horse.horse_type_male || '')}
            </p>
            <p className="text-xs text-gray-600">
              {horse.sire_name && <><span className="font-bold">Sire:</span> {horse.sire_name}</>}
              {horse.sire_name && horse.dam_name && ' · '}
              {horse.dam_name && <><span className="font-bold">Dam:</span> {horse.dam_name}</>}
            </p>
          </div>
          <div className="text-right ml-4 shrink-0">
            <p className="font-bold text-sm">Microchip # {horse.microchip_id || '—'}</p>
            <div className="border border-red-400 rounded px-2 py-0.5 mt-1 text-xs text-red-700 max-w-[200px]">
              <span className="font-semibold">Vices &amp; Disclaimers:</span> {buildVices(horse)}
            </div>
          </div>
        </div>
      </div>

      {/* Owners */}
      <div className="mb-3">
        <p className="font-bold text-xs mb-1">Owner(s):</p>
        <div className="flex flex-wrap gap-6">
          {owners.length === 0 && <p className="text-xs text-gray-500 italic">No current owners on record.</p>}
          {owners.map(own => {
            const a = own.primary_address || {};
            const hasAddress = a.street || a.city || a.state;
            const hasPhone = own.primary_phone;
            const hasEmail = own.primary_email;
            const missingContact = !hasAddress || !hasPhone || !hasEmail;
            return (
              <div key={own.id} className="text-xs">
                <p className="font-semibold">{own.system_name}</p>
                {hasAddress ? (
                  <>
                    {a.street && <p>{a.street}</p>}
                    {(a.city || a.state || a.zip_code) && (
                      <p>{[a.city, a.state, a.zip_code].filter(Boolean).join(', ')}</p>
                    )}
                  </>
                ) : null}
                {hasPhone ? <p>{own.primary_phone}</p> : null}
                {hasEmail ? <p>{own.primary_email}</p> : null}
                {missingContact && !isApproved && (
                  <a
                    href={`/crm/${own.crm_class === 'account' ? 'accounts' : 'contacts'}/${own.id}?tab=contact-details`}
                    className="text-blue-600 underline text-[10px] block mt-0.5">
                    + Add missing contact info
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Departure Details */}
      <div className="mb-3 border border-gray-400">
        <div className="bg-gray-200 px-2 py-1 font-bold text-xs border-b border-gray-400">DEPARTURE DETAILS:</div>
        <div className="grid grid-cols-3 text-xs">
          {/* Row 1: labels */}
          <div className="p-2 border-b border-r border-gray-300">
            <span className="font-bold">Departure Date:</span> {fmtDate(travel.start_date || travel.next_due_date)}
          </div>
          <div className="p-2 border-b border-r border-gray-300">
            <span className="font-bold">Vanned By:</span> {travel.notes || '—'}
          </div>
          <div className="p-2 border-b border-gray-300 flex flex-col gap-1">
            <span className="font-bold">Departure Notes:</span>
            {!isApproved && editingNotes ? (
              <div className="flex gap-1 items-start">
                <textarea
                  value={depNotes}
                  onChange={e => setDepNotes(e.target.value)}
                  className="text-xs border rounded px-1 py-0.5 flex-1 resize-none"
                  rows={2}
                  autoFocus
                />
                <button onClick={() => setEditingNotes(false)} className="text-primary text-[10px] shrink-0">✓</button>
              </div>
            ) : (
              <span
                onClick={() => !isApproved && setEditingNotes(true)}
                className={`text-gray-500 italic min-h-[20px] block ${!isApproved ? 'cursor-pointer hover:text-gray-700' : ''}`}
                title={isApproved ? '' : 'Click to edit'}>
                {depNotes || (!isApproved ? 'Click to add notes…' : '')}
              </span>
            )}
          </div>
          {/* Row 2: Ship From / Ship To */}
          <div className="p-2 border-r border-gray-300">
            <p className="font-bold">Ship From:</p>
            <p className="font-semibold">{tAddr.name}</p>
            {tAddr.street && <p>{tAddr.street}</p>}
            {(tAddr.city || tAddr.state || tAddr.zip) && (
              <p>{[tAddr.city, tAddr.state, tAddr.zip].filter(Boolean).join(', ')}</p>
            )}
            {tAddr.phone && <p>{tAddr.phone}</p>}
          </div>
          <div className="p-2 border-r border-gray-300">
            <p className="font-bold">Ship To:</p>
            <p className="font-semibold">{destName}</p>
            {destAddr.street ? (
              <>
                <p>{destAddr.street}</p>
                {(destAddr.city || destAddr.state || destAddr.zip_code) && (
                  <p>{[destAddr.city, destAddr.state, destAddr.zip_code].filter(Boolean).join(', ')}</p>
                )}
                {destPhone && <p>{destPhone}</p>}
              </>
            ) : !isApproved && destEnt?.id ? (
              <a
                href={`/crm/accounts/${destEnt.id}`}
                className="text-[10px] text-blue-600 underline">
                + Add address to profile
              </a>
            ) : !isApproved ? (
              <input
                type="text"
                placeholder="Enter address…"
                className="text-xs border rounded px-1 py-0.5 w-full mt-1"
              />
            ) : null}
          </div>
          <div className="p-2" />
        </div>
      </div>

      {/* Breeding Records */}
      {covers.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-bold text-xs">Breeding Records</p>
            {!isApproved && (
            <button
              onClick={() => navigate(`/horses/${horse.id}?tab=breeding`)}
              title="Edit breeding records"
              className="text-gray-400 hover:text-primary">
              <Pencil className="w-3 h-3" />
            </button>
            )}
          </div>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {['Covering Sire','Cover Date','Cover Outcome','Pregnancy Status','Fertility',''].map((h, i) => (
                  <th key={i} className={i < 5 ? th : 'w-6'}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleCovers.map((c, visIdx) => {
                const origIdx = covers.indexOf(c);
                return (
                  <tr key={origIdx}>
                    <td className={td}>{c.covering_sire || '—'}</td>
                    <td className={td}>{fmtDate(c.cover_date)}</td>
                    <td className={td}>{c.outcome || '—'}</td>
                    <td className={td}>{c.pregnancy_status || '—'}</td>
                    <td className={td}>{c.fertility_status || '—'}</td>
                    <td className="px-1 align-middle">
                      {!isApproved && (
                      <button onClick={() => setDeletedBreeding(s => new Set([...s, origIdx]))}
                        className="text-gray-300 hover:text-red-500" title="Remove row">
                        <Trash2 className="w-3 h-3" />
                      </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Health Records */}
      <div className="mb-3">
          <div className="flex items-center gap-2 mb-1">
          <p className="font-bold text-xs">
            Health Records{dateRange ? `: ${dateRange}` : ''}
          </p>
          {!isApproved && (
          <button
            onClick={() => navigate(`/horses/${horse.id}?tab=farmfolio`)}
            title="Edit health records"
            className="text-gray-400 hover:text-primary">
            <Pencil className="w-3 h-3" />
          </button>
          )}
          </div>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {['Category','Description','Date / Time','By','Comments',''].map((h, i) => (
                <th key={i} className={i < 5 ? th : 'w-6'}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleHealth.length === 0 && (
              <tr><td colSpan={6} className={`${td} text-center text-gray-400 italic`}>No health records for this date range.</td></tr>
            )}
            {visibleHealth.map((r, visIdx) => {
              const origIdx = health.indexOf(r);
              const desc = `${r.type || ''}${r.name ? ` – ${r.name}` : ''}${r.dosage ? ` (${r.dosage}${r.dosage_unit ? ' ' + r.dosage_unit : ''})` : ''}`;
              const dt = fmtDateTime(r.log_date, r.log_time);
              const by = r.performed_by ? `${r.performed_by}${r.performed_by_type ? ` (${r.performed_by_type.toLowerCase()})` : ''}` : '';
              return (
                <tr key={origIdx} className={visIdx % 2 === 0 ? '' : 'bg-gray-50'}>
                  <td className={td}>{r.category}</td>
                  <td className={td}>{desc}</td>
                  <td className={td}>{dt}</td>
                  <td className={td}>{by}</td>
                  <td className={td}>{r.comments || ''}</td>
                  <td className="px-1 align-middle">
                    {!isApproved && (
                    <button onClick={() => setDeletedHealth(s => new Set([...s, origIdx]))}
                      className="text-gray-300 hover:text-red-500" title="Remove row">
                      <Trash2 className="w-3 h-3" />
                    </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Signature line */}
      <div className="mt-8 pt-4 border-t border-gray-300">
        <div className="grid grid-cols-3 gap-4 text-xs">
          <div><div className="border-b border-black mb-1 h-5" /><p>Signature</p></div>
          <div><div className="border-b border-black mb-1 h-5" /><p>Name</p></div>
          <div><div className="border-b border-black mb-1 h-5" /><p>Date</p></div>
        </div>
      </div>

      {/* Powered by Green Door */}
      <div className="mt-8 pt-4 border-t border-gray-100 flex justify-center">
        <img src={GREEN_DOOR_LOGO} alt="Powered by Green Door" className="w-1/4 opacity-50" />
      </div>
    </div>
    </div>
  );
}
